import React, { useState, useCallback } from 'react';
import { Card, Badge, Input } from '../../../components/ui';
import { Trophy, Calendar, RefreshCw, CheckCircle } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';
import { fechaLocalYMD } from '../../../utils/date';

interface NumeroGanadorConSorteo {
  id: number;
  sorteo_id: string;
  sorteo_nombre: string;
  tipo_id: string;
  fecha: string;
  hora_cierre: string;
  num1: string;
  num2: string | null;
  num3: string | null;
  tipo_premio: string | null;
  tipo_monazos: string | null;
  paga_reventado: boolean | null;
  registrado_por: string;
  created_at: string;
  verificado: boolean;
}

const TIPOS_CONFIG: Record<string, { label: string; color: string }> = {
  NORMAL:    { label: 'Normal',    color: 'bg-blue-600' },
  REVENTADO: { label: 'Reventado', color: 'bg-orange-600' },
  MEGA:      { label: 'Mega',      color: 'bg-red-600' },
  MONAZOS:   { label: 'Monazos',   color: 'bg-purple-600' },
  FECHAS:    { label: 'Fechas',    color: 'bg-green-600' },
  PARLEY:    { label: 'Parley',    color: 'bg-pink-600' },
};

function formatearNumero(g: NumeroGanadorConSorteo): string {
  const p2 = (s: string) => s.padStart(2, '0');
  switch (g.tipo_id) {
    case 'NORMAL':
      return p2(g.num1);
    case 'REVENTADO': {
      const base = p2(g.num1);
      return g.tipo_premio ? `${base} (${g.tipo_premio})` : base;
    }
    case 'MEGA':
      return g.num2 ? `${p2(g.num1)} / ${p2(g.num2)}` : p2(g.num1);
    case 'MONAZOS': {
      const base = g.num1.padStart(3, '0');
      return g.tipo_monazos ? `${base} (${g.tipo_monazos})` : base;
    }
    case 'FECHAS':
      return g.num2 ? `${p2(g.num1)}-${p2(g.num2)}` : p2(g.num1);
    case 'PARLEY': {
      const partes = [g.num1];
      if (g.num2) partes.push(g.num2);
      if (g.num3) partes.push(g.num3);
      return partes.map(p2).join(' + ');
    }
    default:
      return g.num1;
  }
}

export const LocalGanadoresPage: React.FC = () => {
  const hoy = fechaLocalYMD();
  const [desde, setDesde] = useState(hoy);
  const [hasta, setHasta] = useState(hoy);
  const [ganadores, setGanadores] = useState<NumeroGanadorConSorteo[]>([]);
  const [loading, setLoading] = useState(false);
  const [buscado, setBuscado] = useState(false);
  const [error, setError] = useState('');

  const cargarGanadores = useCallback(async () => {
    setLoading(true);
    setError('');
    setBuscado(true);
    try {
      let url = '/ganadores?';
      if (desde === hasta) {
        url += `fecha=${desde}`;
      } else {
        url += `desde=${desde}&hasta=${hasta}`;
      }
      const data = await fetchAPI<NumeroGanadorConSorteo[]>(url);
      setGanadores(data);
    } catch (e: any) {
      setError(e.message || 'Error al cargar ganadores');
    } finally {
      setLoading(false);
    }
  }, [desde, hasta]);

  // Agrupar por fecha para visualización de rangos
  const porFecha = ganadores.reduce<Record<string, NumeroGanadorConSorteo[]>>((acc, g) => {
    (acc[g.fecha] = acc[g.fecha] || []).push(g);
    return acc;
  }, {});
  const fechasOrdenadas = Object.keys(porFecha).sort((a, b) => b.localeCompare(a));

  return (
    <div className="space-y-6">
      {/* Encabezado */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Números Ganadores</h1>
          <p className="text-gray-500">Consulta los ganadores registrados por su banca</p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}

      {/* Filtros */}
      <Card>
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
            <Input
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              leftIcon={<Calendar size={18} />}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
            <Input
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              leftIcon={<Calendar size={18} />}
            />
          </div>
          <button
            onClick={cargarGanadores}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Consultar
          </button>
        </div>
      </Card>

      {/* Cargando */}
      {loading && (
        <div className="text-center py-12 text-gray-500">Cargando ganadores...</div>
      )}

      {/* Sin resultados */}
      {!loading && buscado && ganadores.length === 0 && (
        <Card>
          <div className="text-center py-10 text-gray-400">
            <Trophy size={40} className="mx-auto mb-3 opacity-30" />
            <p>No hay números ganadores en el período seleccionado.</p>
          </div>
        </Card>
      )}

      {/* Resultados agrupados por fecha */}
      {!loading && fechasOrdenadas.map((fecha) => (
        <Card key={fecha}>
          <h2 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Calendar size={16} />
            {fecha}
            <span className="text-xs text-gray-400 font-normal">
              ({porFecha[fecha].length} sorteo{porFecha[fecha].length !== 1 ? 's' : ''})
            </span>
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-4">Sorteo</th>
                  <th className="py-2 pr-4">Tipo</th>
                  <th className="py-2 pr-4">Cierre</th>
                  <th className="py-2 pr-4">Número Ganador</th>
                  <th className="py-2">Estado</th>
                </tr>
              </thead>
              <tbody>
                {porFecha[fecha]
                  .sort((a, b) => (a.hora_cierre || '').localeCompare(b.hora_cierre || ''))
                  .map((g) => {
                    const tipoCfg = TIPOS_CONFIG[g.tipo_id] || { label: g.tipo_id, color: 'bg-gray-500' };
                    return (
                      <tr key={g.id} className="border-b last:border-0 hover:bg-gray-50">
                        <td className="py-2 pr-4 font-medium text-gray-900">{g.sorteo_nombre}</td>
                        <td className="py-2 pr-4">
                          <span className={`px-2 py-0.5 rounded text-xs text-white font-medium ${tipoCfg.color}`}>
                            {tipoCfg.label}
                          </span>
                        </td>
                        <td className="py-2 pr-4 font-mono text-gray-600">
                          {g.hora_cierre ? g.hora_cierre.slice(0, 5) : '—'}
                        </td>
                        <td className="py-2 pr-4">
                          <span className="font-bold text-lg font-mono text-blue-700">
                            {formatearNumero(g)}
                          </span>
                          {g.paga_reventado && (
                            <span className="ml-2 text-xs text-orange-600 font-medium">+ Reventado</span>
                          )}
                        </td>
                        <td className="py-2">
                          {g.verificado ? (
                            <Badge variant="success">
                              <CheckCircle size={12} className="inline mr-1" />
                              Verificado
                            </Badge>
                          ) : (
                            <Badge variant="warning">Pendiente</Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </Card>
      ))}
    </div>
  );
};

export default LocalGanadoresPage;
